import { type ReactNode } from "react";
import {
  EyeOutlined,
  AuditOutlined,
  EditOutlined,
  DeleteOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { HistoryIcon, RecallSignIcon } from "@/shared/assets/svg/icons";
import { RecordStatus } from "@shared/ui";

export type SectionThreeActionKey =
  | "view"
  | "sendToSign"
  | "rejectSign" // inputter recall from signing
  | "rollback"
  | "edit"
  | "delete"
  | "versions";

export interface ActionItem {
  key: string;
  label: string;
  icon: ReactNode;
  action: string;
}

export const getSectionThreeActions = (
  status?: string,
): SectionThreeActionKey[] => {
  switch (status) {
    case "NEW":
      return ["view", "edit", "sendToSign", "delete"];
    case "AWAITING_SIGNATURE":
      return ["view", "rejectSign"]; // show recall for inputter; signer gets sign/reject controls separately
    case "REJECTED":
      return ["view", "rollback"];
    case "SIGNED":
      return ["view", "versions", "edit"];
    default:
      return ["view"];
  }
};

export const getSectionThreeActionItems = (
  status: RecordStatus,
  record?: any,
  hasEditPermission = false,
  isVersionsEnabled = true,
): ActionItem[] => {
  const actions = getSectionThreeActions(status);
  const editOnlyActions: SectionThreeActionKey[] = [
    "sendToSign",
    "delete",
    "edit",
    "rejectSign",
    "rollback",
  ];

  const actionMap = {
    view: {
      key: "view",
      label: "Открыть запись",
      icon: <EyeOutlined />,
      action: "VIEW",
    },
    versions: {
      key: "versions",
      label: "Версии",
      icon: <HistoryIcon />,
      action: "VERSIONS",
    },
    edit: {
      key: "edit",
      label: "Редактировать",
      icon: <EditOutlined />,
      action: "EDIT",
    },
    sendToSign: {
      key: "send",
      label: "Отправить на подписание",
      icon: <AuditOutlined />,
      action: "SEND",
    },
    delete: {
      key: "delete",
      label: "Удалить",
      icon: <DeleteOutlined />,
      action: "DELETE",
    },
    rejectSign: {
      key: "recall",
      label: "Отозвать с подписания",
      icon: <RecallSignIcon />,
      action: "RECALL",
    },
    rollback: {
      key: "return",
      label: "Вернуть в работу",
      icon: <RollbackOutlined />,
      action: "RETURN",
    },
  };

  // Фильтруем действия в зависимости от статуса и пользователя
  const filteredActions = actions.filter((action: SectionThreeActionKey) => {
    // view всегда доступен
    if (action === "view") return false; // view уже есть в baseItems

    if (editOnlyActions.includes(action) && !hasEditPermission) {
      return false;
    }

    // Версии показываем только если есть currentVersionId (как в API)
    if (action === "versions") {
      return (
        isVersionsEnabled && Boolean(record?.info?.currentVersionId)
      );
    }

    // Если статус NEW, показываем edit, delete, sendToSign для любого пользователя с разрешением
    if (status === "NEW" && (action === "edit" || action === "delete" || action === "sendToSign")) {
      return true;
    }

    // Для SIGNED статуса показываем edit
    if (status === "SIGNED" && action === "edit") {
      return true;
    }

    // Для REJECTED показываем rollback
    if (status === "REJECTED" && action === "rollback") {
      return true;
    }

    // Для AWAITING_SIGNATURE показываем rejectSign
    if (status === "AWAITING_SIGNATURE" && action === "rejectSign") {
      return true;
    }

    return false;
  });

  return filteredActions
    .map((action: SectionThreeActionKey) => actionMap[action])
    .filter(Boolean);
};
